import {
  PricebookDiffLine,
  PricebookDiffLinePrice,
  PricebookItemPreviewRow,
} from "../../../../types/price-book.type";
import { PricebookLine } from "./lineEdits";

/** Why a line is in the confirmation besides a new price. */
export type PriceChangeNote = "added" | "removed" | "enabled" | "disabled";

/** One line of the save confirmation: what it bills at now and what it will bill at. */
export interface PriceChangeRow {
  productCode: string;
  productName: string;
  note: PriceChangeNote | null;
  beforeInclVat: number | null;
  beforeExVat: number | null;
  /** Null for a line the save takes off the book. */
  afterInclVat: number | null;
  afterExVat: number | null;
}

const statusNote = (
  stored: PricebookLine,
  current: PricebookLine,
): PriceChangeNote | null => {
  if (stored.status === current.status) return null;
  return current.status === "enable" ? "enabled" : "disabled";
};

/**
 * Everything the save is about to change, in the table's order and then the removed lines:
 * the server's preview prices each line the save writes, next to what is stored now. A line
 * whose line predates the discount model has no stored incl/ex price, so it is compared with
 * the legacy price it bills at. Lines whose prices and status stay the same are left out.
 */
export const buildPriceChangeRows = (
  storedLines: PricebookLine[],
  lines: PricebookLine[],
  previewRows: PricebookItemPreviewRow[],
): PriceChangeRow[] => {
  const storedByCode = new Map(
    storedLines.map((line) => [line.productCode, line]),
  );
  const previewByCode = new Map(
    previewRows.map((row) => [row.product_code, row]),
  );
  const rows: PriceChangeRow[] = [];

  lines.forEach((line) => {
    const preview = previewByCode.get(line.productCode);
    if (!preview) return;
    const stored = storedByCode.get(line.productCode);
    const beforeInclVat = stored
      ? preview.current_price_incl_vat ?? stored.storedPriceInclVat
      : null;
    const beforeExVat = stored
      ? preview.current_price_ex_vat ?? stored.storedPriceExVat
      : null;
    const note = stored ? statusNote(stored, line) : "added";
    const isPriceChanged =
      beforeInclVat !== preview.price_incl_vat ||
      beforeExVat !== preview.price_ex_vat;
    if (!note && !isPriceChanged) return;
    rows.push({
      productCode: line.productCode,
      productName: line.productName,
      note,
      beforeInclVat,
      beforeExVat,
      afterInclVat: preview.price_incl_vat,
      afterExVat: preview.price_ex_vat,
    });
  });

  const currentCodes = new Set(lines.map((line) => line.productCode));
  storedLines.forEach((stored) => {
    if (currentCodes.has(stored.productCode)) return;
    rows.push({
      productCode: stored.productCode,
      productName: stored.productName,
      note: "removed",
      beforeInclVat: stored.storedPriceInclVat,
      beforeExVat: stored.storedPriceExVat,
      afterInclVat: null,
      afterExVat: null,
    });
  });

  return rows;
};

/** An on/off switch between the source's line and the draft's; null when both agree. */
const diffStatusNote = (
  sourceLine: PricebookDiffLinePrice,
  draftLine: PricebookDiffLinePrice,
): PriceChangeNote | null => {
  if (sourceLine.status === draftLine.status) return null;
  return draftLine.status === "enable" ? "enabled" : "disabled";
};

const resolveDiffNote = (line: PricebookDiffLine): PriceChangeNote | null => {
  if (!line.old) return "added";
  if (!line.new) return "removed";
  return diffStatusNote(line.old, line.new);
};

/**
 * The draft comparison as change rows: the source's prices before, the draft's after. A product
 * whose master row is gone keeps its code as its name.
 */
export const buildDraftChangeRows = (
  lines: PricebookDiffLine[],
): PriceChangeRow[] =>
  lines.map((line) => ({
    productCode: line.product_code,
    productName: line.product_name ?? line.product_code,
    note: resolveDiffNote(line),
    beforeInclVat: line.old?.price_incl_vat ?? null,
    beforeExVat: line.old?.price_ex_vat ?? null,
    afterInclVat: line.new?.price_incl_vat ?? null,
    afterExVat: line.new?.price_ex_vat ?? null,
  }));
